import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Text, Layer } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, InfoNotice, DatetimeCalendar, FlexibleSelectDropdown,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { fetchAdminDemoLinkById } from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
  SearchInput,
} from '../../../Partial/Product/SharedComponents';

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { api: 'date_desc', display: 'Date (Newest first)' },
  { api: 'date_asc', display: 'Date (Oldest first)' },
  { api: 'name_asc', display: 'Name A → Z' },
  { api: 'name_desc', display: 'Name Z → A' },
  { api: 'email_asc', display: 'Email A → Z' },
  { api: 'email_desc', display: 'Email Z → A' },
];

const HEADER = [
  { display: '#', uid: 'index', size: '5%' },
  { display: 'Name', uid: 'name', size: '20%' },
  { display: 'Email', uid: 'email', size: '23%' },
  { display: 'YouTube Channel', uid: 'channel', size: '35%' },
  { display: 'Date', uid: 'date', size: '17%' },
];

function applyFilters(submissions, search, dateFrom, dateTo) {
  let result = submissions;

  if (search) {
    const q = search.toLowerCase();
    result = result.filter(
      (s) => (s.fullName || '').toLowerCase().includes(q)
        || (s.email || '').toLowerCase().includes(q)
        || (s.ytChannelUrl || '').toLowerCase().includes(q),
    );
  }

  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    result = result.filter((s) => s.collectedAt && new Date(s.collectedAt) >= from);
  }

  if (dateTo) {
    const to = new Date(`${dateTo}T23:59:59`);
    result = result.filter((s) => s.collectedAt && new Date(s.collectedAt) <= to);
  }

  return result;
}

function applySort(submissions, sortKey) {
  const arr = [...submissions];
  switch (sortKey) {
    case 'date_asc':
      arr.sort((a, b) => new Date(a.collectedAt || 0) - new Date(b.collectedAt || 0));
      break;
    case 'name_asc':
      arr.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      break;
    case 'name_desc':
      arr.sort((a, b) => (b.fullName || '').localeCompare(a.fullName || ''));
      break;
    case 'email_asc':
      arr.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      break;
    case 'email_desc':
      arr.sort((a, b) => (b.email || '').localeCompare(a.email || ''));
      break;
    case 'date_desc':
    default:
      arr.sort((a, b) => new Date(b.collectedAt || 0) - new Date(a.collectedAt || 0));
  }
  return arr;
}

const AdminDemoSubmissionsPage = ({
  small,
  location,
  adminToken,
  fetchLinkById,
}) => {
  const { linkId } = useParams();
  const navState = useLocation().state;
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const padHorizontal = small ? '1rem' : '2rem';

  const [link, setLink] = React.useState(navState?.link || null);
  const [loading, setLoading] = React.useState(!navState?.link);

  const [searchInput, setSearchInput] = React.useState('');
  const [sortKey, setSortKey] = React.useState('date_desc');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [pendingDateFrom, setPendingDateFrom] = React.useState('');
  const [pendingDateTo, setPendingDateTo] = React.useState('');
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);

  React.useEffect(() => {
    if (!navState?.link && linkId && adminToken) {
      setLoading(true);
      fetchLinkById(adminToken, linkId)
        .then((data) => setLink(data))
        .catch(() => setLink(null))
        .finally(() => setLoading(false));
    }
  }, [linkId, adminToken, navState, fetchLinkById]);

  const handleSearch = React.useCallback((_, value) => {
    setSearchInput(value || '');
    setCurrentPage(0);
  }, []);

  const handleDateRangeSelect = React.useCallback((from, to) => {
    setPendingDateFrom(from || '');
    setPendingDateTo(to || '');
  }, []);

  const handleCloseDatePicker = React.useCallback(() => {
    setDateFrom(pendingDateFrom);
    setDateTo(pendingDateTo);
    setCurrentPage(0);
    setShowDatePicker(false);
  }, [pendingDateFrom, pendingDateTo]);

  const handleOpenDatePicker = React.useCallback(() => {
    setPendingDateFrom(dateFrom);
    setPendingDateTo(dateTo);
    setShowDatePicker(true);
  }, [dateFrom, dateTo]);

  const handleReset = React.useCallback(() => {
    setSearchInput('');
    setSortKey('date_desc');
    setDateFrom('');
    setDateTo('');
    setPendingDateFrom('');
    setPendingDateTo('');
    setCurrentPage(0);
  }, []);

  const allSubmissions = link?.submissions || [];

  const filtered = React.useMemo(
    () => applyFilters(allSubmissions, searchInput, dateFrom, dateTo),
    [allSubmissions, searchInput, dateFrom, dateTo],
  );

  const sorted = React.useMemo(
    () => applySort(filtered, sortKey),
    [filtered, sortKey],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const hasActiveFilters = searchInput || dateFrom || dateTo || sortKey !== 'date_desc';

  const formatDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
    })
    : '—');

  const dateRangeLabel = (() => {
    if (dateFrom && dateTo) return `${dateFrom} – ${dateTo}`;
    if (dateFrom) return `From ${dateFrom}`;
    if (dateTo) return `To ${dateTo}`;
    return 'Date range';
  })();

  const dateRangeValue = React.useMemo(() => ({
    from: pendingDateFrom ? new Date(`${pendingDateFrom}T12:00:00`) : undefined,
    to: pendingDateTo ? new Date(`${pendingDateTo}T12:00:00`) : undefined,
  }), [pendingDateFrom, pendingDateTo]);

  const renderCell = (uid, row, idx) => {
    if (uid === 'index') {
      return (
        <Text size="0.85rem" color={page.secondaryText}>
          {currentPage * PAGE_SIZE + idx + 1}
        </Text>
      );
    }
    if (uid === 'name') {
      return (
        <Text size="0.85rem" color={page.tableText} weight={500}>
          {row.fullName || '—'}
        </Text>
      );
    }
    if (uid === 'email') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {row.email || '—'}
        </Text>
      );
    }
    if (uid === 'channel') {
      return row.ytChannelUrl ? (
        <a
          href={row.ytChannelUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#e53e3e', fontSize: '0.82rem', wordBreak: 'break-all' }}
        >
          {row.ytChannelUrl}
        </a>
      ) : (
        <Text size="0.82rem" color={page.secondaryText}>—</Text>
      );
    }
    if (uid === 'date') {
      return (
        <Text size="0.82rem" color={page.secondaryText}>
          {formatDate(row.collectedAt)}
        </Text>
      );
    }
    return null;
  };

  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <SearchInput
        small={small}
        value={searchInput}
        handleSearch={handleSearch}
        placeholder="Search name, email, channel…"
        debounce={250}
      />
      <AppButton
        overrideHover
        level="tableControls"
        height="var(--table-controls-height)"
        color={(dateFrom || dateTo) ? button.primaryText : page.inputText}
        bgColor={(dateFrom || dateTo) ? page.highlight : page.inputBg}
        label={dateRangeLabel}
        onClick={handleOpenDatePicker}
        style={{ whiteSpace: 'nowrap', border: `1px solid ${page.inputBorder || page.tableBorder}` }}
      />
      <Box>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => { setSortKey(value || 'date_desc'); setCurrentPage(0); }}
          flexibleOptions={SORT_OPTIONS}
          noSelectionLabel="Sort by…"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={SORT_OPTIONS.find((o) => o.api === sortKey)?.display || 'Sort by…'}
          withReset={sortKey !== 'date_desc'}
        />
      </Box>
      <AppButton
        overrideHover
        disabled={!hasActiveFilters}
        onClick={handleReset}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={button.primaryBg}
        label="Reset"
      />
    </Box>
  );

  const renderContents = () => {
    if (loading) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          loading
          label="Loading submissions…"
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    if (!link) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          loading={false}
          label="Campaign not found."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    if (sorted.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          loading={false}
          showRefreshButton={!!hasActiveFilters}
          refreshQuery={handleReset}
          buttonHighlight={button.primaryBg}
          noDataLabel={allSubmissions.length === 0
            ? 'No submissions yet for this campaign.'
            : 'No submissions match your filters.'}
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {paginated.map((row, idx) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={row.email || idx}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            pad={{ vertical: '0.75rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && display && (
                  <Text size="0.875rem" color={page.tableHeader}>{`${display}:`}</Text>
                )}
                {renderCell(uid, row, idx)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const campaignName = link?.name || 'Campaign';

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: `${campaignName} — Submissions`,
        subTitle: 'Demo · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <Box pad={{ horizontal: padHorizontal }}>
          <AppButton
            plain
            overrideHover
            level="bare"
            label="← Back to Campaigns"
            color={page.highlight}
            onClick={() => navigate('/admin/demo-links')}
          />
        </Box>

        {link && (
          <InfoNotice
            header={`${allSubmissions.length} ${allSubmissions.length === 1 ? 'submission' : 'submissions'} total`}
            text={`Campaign link: ${window.location.origin}/demo/${link.token}`}
          />
        )}

        <Box pad={{ horizontal: padHorizontal }} width="100%">
          <TableWrapper
            small={small}
            header={`Submissions${sorted.length !== allSubmissions.length ? ` (${sorted.length} filtered)` : ''}`}
            boxPad={tablePadding(small)}
            isPaged={sorted.length > PAGE_SIZE}
            pageCount={totalPages}
            activePage={currentPage}
            setActivePage={setCurrentPage}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>

      {showDatePicker && (
        <Layer
          onEsc={handleCloseDatePicker}
          onClickOutside={handleCloseDatePicker}
        >
          <DatetimeCalendar
            hideTimeZone
            showCloseButton
            showTimeSelect={false}
            handleClose={handleCloseDatePicker}
            small={small}
            calendarMode="range"
            setDateRange={handleDateRangeSelect}
            dateRangeValue={dateRangeValue}
          />
        </Layer>
      )}
    </AdminNavWrapper>
  );
};

AdminDemoSubmissionsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  adminToken: PropTypes.string.isRequired,
  fetchLinkById: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  adminToken: state.adminAuth?.adminToken || '',
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchLinkById: fetchAdminDemoLinkById,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AdminDemoSubmissionsPage);
